import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FiTrash2, FiShoppingBag, FiAlertCircle, FiCheckCircle, FiArrowRight } from "react-icons/fi";
import { useCart } from "../context/CartContext";
import { formatCurrency } from "../utils/currency";
import { validateCart } from "../utils/cartValidation";
import QuantitySelector from "../components/ui/QuantitySelector";
import Button from "../components/ui/Button";

const CartPage = () => {
  const { items, removeFromCart, updateQuantity, subtotal, savings, clearCart } = useCart();
  const navigate = useNavigate();
  const validation = validateCart(subtotal);
  const shippingCost = subtotal >= 99 ? 0 : 20;
  const total = subtotal + shippingCost;

  if (items.length === 0) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-5 text-center px-4">
        <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center">
          <FiShoppingBag size={42} className="text-gray-400" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Your cart is empty</h2>
          <p className="text-gray-500 mt-2 max-w-sm">Looks like you haven't added anything yet. Browse our compact electronics collection!</p>
        </div>
        <Link to="/shop" className="btn-primary">Start Shopping</Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Shopping Cart</h1>
        <button onClick={clearCart} className="text-sm text-red-500 hover:text-red-700 font-medium transition-colors">
          Clear Cart
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Cart items */}
        <div className="lg:col-span-2 space-y-4">
          <AnimatePresence>
            {items.map((item) => (
              <motion.div
                key={item.id}
                layout
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.2 }}
                className="card p-5 flex gap-4"
              >
                <Link to={`/product/${item.slug}`}>
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-24 h-24 md:w-28 md:h-28 object-cover rounded-xl bg-gray-100 flex-shrink-0"
                  />
                </Link>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <Link to={`/product/${item.slug}`}>
                      <h3 className="font-semibold text-gray-900 hover:text-brand-600 transition-colors leading-snug">
                        {item.title}
                      </h3>
                    </Link>
                    <button
                      onClick={() => removeFromCart(item.id)}
                      className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                      aria-label="Remove item"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5">SKU: {item.sku}</p>

                  <div className="flex items-end justify-between mt-3 flex-wrap gap-3">
                    <div>
                      <p className="text-xs text-gray-400 mb-1.5">Qty</p>
                      <QuantitySelector
                        quantity={item.quantity}
                        onChange={(qty) => updateQuantity(item.id, qty)}
                        max={item.stock}
                      />
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-400">Unit: {formatCurrency(item.price)}</p>
                      <p className="font-bold text-gray-900 text-lg">
                        {formatCurrency(item.price * item.quantity)}
                      </p>
                      {item.compareAtPrice && (
                        <p className="text-xs text-emerald-600">
                          Saving {formatCurrency((item.compareAtPrice - item.price) * item.quantity)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          <Link to="/shop" className="inline-flex items-center gap-2 text-brand-600 font-medium hover:gap-3 transition-all text-sm mt-2">
            ← Continue Shopping
          </Link>
        </div>

        {/* Order summary — sticky */}
        <div className="lg:col-span-1">
          <div className="card p-6 sticky top-24 space-y-4">
            <h2 className="font-bold text-lg text-gray-900">Order Summary</h2>

            {/* Line items summary */}
            <div className="space-y-2 text-sm">
              {items.map((item) => (
                <div key={item.id} className="flex justify-between text-gray-600">
                  <span className="line-clamp-1 max-w-[180px]">{item.title} × {item.quantity}</span>
                  <span>{formatCurrency(item.price * item.quantity)}</span>
                </div>
              ))}
            </div>

            <div className="border-t border-gray-100 pt-4 space-y-2">
              {savings > 0 && (
                <div className="flex justify-between text-sm text-emerald-600 font-medium">
                  <span>Total Savings</span>
                  <span>−{formatCurrency(savings)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm text-gray-600">
                <span>Shipping</span>
                {shippingCost === 0
                  ? <span className="text-emerald-600 font-medium">FREE</span>
                  : <span>{formatCurrency(shippingCost)}</span>
                }
              </div>
              {shippingCost > 0 && (
                <p className="text-xs text-gray-400">Add {formatCurrency(99 - subtotal)} more for free shipping</p>
              )}
              <div className="flex justify-between font-bold text-gray-900 text-xl pt-2 border-t border-gray-100">
                <span>Total</span>
                <span>{formatCurrency(total)}</span>
              </div>
            </div>

            {/* Validation notice */}
            <div
              className={`flex items-start gap-2.5 p-3.5 rounded-xl text-sm ${
                validation.valid ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
              }`}
            >
              {validation.valid ? (
                <FiCheckCircle size={16} className="mt-0.5 flex-shrink-0" />
              ) : (
                <FiAlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              )}
              <span>
                {validation.valid ? "Ready to checkout!" : validation.message}
              </span>
            </div>

            <div className="p-3 bg-gray-50 rounded-xl text-xs text-gray-500 space-y-1">
              <p>✓ Free shipping on orders over $99</p>
              <p>✓ Secure checkout — no real payment processed</p>
            </div>

            <Button
              onClick={() => validation.valid && navigate("/checkout")}
              disabled={!validation.valid}
              className="w-full"
            >
              Proceed to Checkout <FiArrowRight size={16} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
